import type { ReactNode } from "react";
import clsx from "clsx";
import { CoverBleed, coverBleedHost } from "../CoverBleed/CoverBleed.js";
import styles from "./DarkHero.module.scss";

export interface DarkHeroProps {
  /** Optional mono eyebrow line (may carry a leading dot/logo node, e.g. the live-dot or a platform mark). */
  eyebrow?: ReactNode;
  /** Optional display title. Paired with {@link DarkHeroProps.titleId} it exposes the hero as a labelled
   *  `region` (the accessible name is the title text). */
  title?: ReactNode;
  /** The `id` wired to the title element AND `aria-labelledby`, so the section is a named landmark region. */
  titleId?: string;
  /** Top-right header slot (a CTA / retry affordance), rendered opposite the eyebrow/title. With
   *  {@link DarkHeroProps.media} it becomes the row's TRAILING column instead, vertically centred beside the
   *  content rather than pinned to the top — see that prop. */
  action?: ReactNode;
  /**
   * An optional LEADING column — cover art, a portrait, a logo lockup — that the eyebrow/title/body all sit
   * beside, rather than beneath.
   *
   * Supplying it switches the hero from a stack (head over body) to a vertically-centred THREE-COLUMN row:
   * `media | (head + children) | action`. That is the reference's featured-banner shape and it cannot be
   * composed from the outside, because the eyebrow and title live inside this component's header — a caller
   * that wanted the art beside the title had to stop passing `eyebrow`/`title` altogether and restate the
   * hero's own type scale in its stylesheet, which is forking the primitive to move a box.
   *
   * Absent ⇒ nothing changes, so no existing consumer moves.
   */
  media?: ReactNode;
  /**
   * Where {@link DarkHeroProps.media} sits.
   *
   * - `row` (default) — the three-column row above: the body sits BESIDE the media.
   * - `head` — the media, the head and `action` form a centred HEADER row, and the body runs the card's full
   *   width beneath it. For a hero whose body is wide — a stat strip and a timeline (the label release card,
   *   ART-335) — which squeezed between the art and the action column reads as a sidebar.
   */
  mediaLayout?: "row" | "head";
  /** A line under the title, inside the head (a date, a meta line). */
  subtitle?: ReactNode;
  /** `lg` — a 32px title, for a hero whose head is the card's headline beside larger art (the release card). */
  titleSize?: "default" | "lg";
  /** Everything below the header — a stat grid, an error banner, a sparkline lead, a brand row, etc. */
  children?: ReactNode;
  /**
   * A resolved band-photo URL to sit BEHIND the hero. Composed exactly as {@link Spotlight} composes its
   * own — the scrim gradient listed FIRST in the background stack so the eyebrow/title/tiles stay legible
   * over an arbitrary photo. Absent → the existing `--ac-gradient-spotlight` fallback is untouched, so no
   * call site moves.
   */
  photoUrl?: string | null;
  /**
   * How {@link DarkHeroProps.photoUrl} is rendered.
   *
   * - `"scrim"` (default) — the photo fills the card sharp, under the readability scrim. For a photo somebody
   *   CHOSE for this slot: a band press shot, framed to be looked at.
   * - `"bleed"` — the photo is blurred, saturated and over-scaled into a soft colour wash under the same
   *   scrim. For artwork that merely IDENTIFIES the record: a square cover cropped to a 9:1 banner is a
   *   meaningless sliver of itself, and a bright one leaves white type sitting on a face. The wash keeps the
   *   thing the tint is for — this banner is stamped with THIS record's colour — and throws away the framing,
   *   which was never legible at this aspect anyway.
   *
   * Both keep the gradient fallback when no photo resolves.
   */
  photoTreatment?: "scrim" | "bleed";
  /**
   * How far the header sits off the body. `"default"` (16px) suits a head that carries a TITLE — a stacked
   * eyebrow+title lockup needs the air to read as its own block. `"tight"` (8px) is for a head that is pure
   * CHROME: a bare brand mark with no title under it, where 16px reads as a logo floating loose above the
   * content rather than as its label (the Streaming Spotify hero, ART-98). `"snug"` (4px) sits a bare LABEL
   * eyebrow just over its figure — the socials mobile hero, where "Total followers" IS the number's label
   * and even 8px reads as a gap rather than a caption (Ryan, ART-184).
   *
   * A prop rather than a caller-side `gap` override in the consumer's own stylesheet: this shell's `.hero`
   * sets `gap` itself, so an override from another CSS module ties on specificity and is decided by
   * whichever module the bundler happens to emit second. Same reason `Modal` owns `bodyLayout`.
   */
  headGap?: "default" | "tight" | "snug";
  className?: string;
}

/**
 * The dark spotlight-hero SHELL (FE-SH-02) — the near-black inverse card with the composed
 * radial+linear gradient, a hatch overlay, and card elevation, plus an OPTIONAL eyebrow / title / action
 * header and free-form children below. Unlike the opinionated {@link Spotlight} (mandatory prose title +
 * delta pill + fixed 3-tile row), this is a composition container: consumers layer their own brand row /
 * sparkline lead / error banner / {@link DarkStatTile} grid inside. Extracted from Music's + Marketing's
 * hand-rolled dark heroes; the Content reach-hero builds on it too.
 */
export function DarkHero({
  eyebrow,
  title,
  titleId,
  action,
  media,
  mediaLayout = "row",
  subtitle,
  titleSize = "default",
  children,
  photoUrl,
  photoTreatment = "scrim",
  headGap = "default",
  className,
}: DarkHeroProps) {
  const hasHeader = eyebrow != null || title != null || action != null;
  // Composed here, not in SCSS: a photo URL is per-band DATA, not a design token, so it must not masquerade
  // as one (the token firewall rightly rejects a phantom `--ac-*`). The scrim above it still resolves from the
  // registry. encodeURI keeps quotes/parens in a signed media URL inside the url(). Mirrors `Spotlight`.
  const bleed = photoTreatment === "bleed";
  // `scrim` composes the scrim ABOVE the photo in one background stack on the section. `bleed` cannot — see
  // `CoverBleed`, which owns that treatment for both release heroes.
  const photo = photoUrl && !bleed
    ? { backgroundImage: `var(--ac-gradient-spotlight-scrim), url("${encodeURI(photoUrl)}")` }
    : undefined;
  const bleedUrl = bleed ? photoUrl : null;
  const headMain = (eyebrow != null || title != null) && (
    <div className={styles.headMain} data-title-size={titleSize === "lg" ? "lg" : undefined}>
      {eyebrow != null && <p className={styles.eyebrow}>{eyebrow}</p>}
      {title != null && (
        <h2 id={titleId} className={clsx(styles.title, titleSize === "lg" && styles.titleLg)}>
          {title}
        </h2>
      )}
      {subtitle != null && <div className={styles.subtitle}>{subtitle}</div>}
    </div>
  );
  const mediaRow = media != null && mediaLayout === "row";

  return (
    <section
      className={clsx(
        styles.hero,
        headGap === "tight" && styles.headGapTight,
        headGap === "snug" && styles.headGapSnug,
        mediaRow && styles.hasMedia,
        photo && styles.hasPhoto,
        bleedUrl && coverBleedHost,
        className,
      )}
      data-head-gap={headGap}
      style={photo}
      aria-labelledby={title != null ? titleId : undefined}
    >
      {/* The gradient ground. Hidden under a bleed, which paints the card itself. */}
      {!bleedUrl && <div className={styles.bg} aria-hidden="true" />}
      <CoverBleed url={bleedUrl} />
      {media != null && mediaLayout === "head" ? (
        // The header row (media | head | action), then the body at full width.
        <>
          <div className={styles.mediaHead}>
            <div className={styles.media}>{media}</div>
            <div className={styles.mediaMain}>{headMain}</div>
            {action}
          </div>
          {children}
        </>
      ) : mediaRow ? (
        // The three-column shape. `action` leaves the header and becomes the trailing column, so it sits
        // centred against the whole row rather than level with the eyebrow — which is what makes a count block
        // read as a peer of the content instead of a corner affordance.
        <>
          <div className={styles.media}>{media}</div>
          <div className={styles.mediaMain}>
            {headMain}
            {children}
          </div>
          {action}
        </>
      ) : (
        <>
          {hasHeader && (
            <div className={styles.head}>
              {headMain}
              {action}
            </div>
          )}
          {children}
        </>
      )}
    </section>
  );
}
